import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Switch,
  InputNumber,
  Select,
  Button,
  Typography,
  Space,
  Alert,
  Spin,
  message,
  Row,
  Col,
  Tooltip,
} from "antd";
import {
  ReloadOutlined,
  SaveOutlined,
  ExperimentOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  getRecipeAssistantSettings,
  putRecipeAssistantSettings,
} from "./api";
import { useAuth } from "../context/AuthContext";

const { Title, Paragraph } = Typography;
const { Option } = Select;

// Profils d'activite proposables comme "secteurs eligibles".
const PROFILE_OPTIONS = [
  { value: "restaurant", label: "Restauration" },
  { value: "fast_food", label: "Fast-food" },
  { value: "bakery", label: "Boulangerie" },
  { value: "retail", label: "Commerce de détail" },
  { value: "production", label: "Production / transformation" },
  { value: "ecommerce", label: "E-commerce" },
  { value: "commercial_services", label: "Services commerciaux" },
  { value: "hotel", label: "Hôtel / hébergement" },
];

const labelWithHint = (label, hint) => (
  <span>
    {label}{" "}
    <Tooltip title={hint}>
      <InfoCircleOutlined style={{ color: "#999" }} />
    </Tooltip>
  </span>
);

/**
 * Page SysAdmin : reglages globaux du moteur d'assistance recettes.
 * Pilote a chaud les seuils (matching, ingredients manquants), le pool
 * inter-entreprises et les secteurs eligibles, sans redeploiement.
 */
const RecipeAssistantSettings = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [notMigrated, setNotMigrated] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const isSuperAdmin = Boolean(
    user?.is_superadmin || user?.role === "SysAdmin"
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getRecipeAssistantSettings();
        if (cancelled) return;
        setDefaults(data?.defaults || null);
        form.setFieldsValue(data?.settings || {});
      } catch (error) {
        if (error?.response?.status === 403) {
          setForbidden(true);
        } else {
          message.error("Erreur lors du chargement des réglages.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (isSuperAdmin) {
      load();
    } else {
      setForbidden(true);
      setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, form]);

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      return; // champs invalides : AntD affiche les erreurs
    }
    setSaving(true);
    try {
      const data = await putRecipeAssistantSettings(values);
      form.setFieldsValue(data?.settings || values);
      setNotMigrated(false);
      message.success("Réglages enregistrés.");
    } catch (error) {
      if (error?.response?.data?.code === "RECIPE_SETTINGS_NOT_MIGRATED") {
        setNotMigrated(true);
        message.warning("Réglages indisponibles : migration requise.");
      } else if (error?.response?.status === 403) {
        message.error("Action réservée au SysAdmin.");
      } else {
        message.error("Échec de l'enregistrement des réglages.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (defaults) {
      form.setFieldsValue(defaults);
      message.info("Valeurs par défaut chargées — pensez à enregistrer.");
    }
  };

  if (forbidden) {
    return (
      <Alert
        type="error"
        showIcon
        message="Accès réservé au SysAdmin"
        description="Cette page de configuration n'est accessible qu'aux super-administrateurs."
        style={{ margin: 24 }}
      />
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 860, margin: "0 auto" }}>
      <Space align="center" style={{ marginBottom: 4 }}>
        <ExperimentOutlined style={{ fontSize: 22 }} />
        <Title level={3} style={{ margin: 0 }}>
          Moteur d'assistance recettes — Réglages
        </Title>
      </Space>
      <Paragraph type="secondary">
        Paramètres globaux des suggestions (recettes &amp; ingrédients).
        Modifiables à chaud, appliqués sans redéploiement.
      </Paragraph>

      {notMigrated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Table de réglages absente"
          description="Exécutez la migration 20260628_add_recipe_assistant_settings.sql pour persister les réglages en base."
        />
      )}

      {loading ? (
        <Spin />
      ) : (
        <Form form={form} layout="vertical">
          <Card
            size="small"
            title="Couche communautaire (pool inter-entreprises)"
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              name="pool_enabled"
              label="Pool activé — tout le secteur participe par défaut"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="k_anonymity"
                  label={labelWithHint(
                    "Seuil d'affichage (k‑anonymat)",
                    "Nombre minimal d'entreprises devant partager une recette/ingrédient pour qu'elle soit affichée dans le pool. 1 = tout partagé immédiatement."
                  )}
                  rules={[{ required: true, message: "Requis" }]}
                >
                  <InputNumber min={1} max={1000} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="eligible_profiles"
                  label="Secteurs éligibles"
                  rules={[{ required: true, message: "Au moins un secteur" }]}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Profils d'activité"
                  >
                    {PROFILE_OPTIONS.map((option) => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card
            size="small"
            title="Pertinence (recherche de recettes)"
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              name="search_threshold"
              label={labelWithHint(
                "Seuil de similarité",
                "Entre 0 et 1. Plus la valeur est haute, plus les suggestions sont strictes. Défaut 0.62."
              )}
              rules={[{ required: true, message: "Requis" }]}
            >
              <InputNumber min={0} max={1} step={0.01} style={{ width: 220 }} />
            </Form.Item>
          </Card>

          <Card
            size="small"
            title="Ingrédients manquants (co-occurrence)"
            style={{ marginBottom: 16 }}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="missing_min_support"
                  label={labelWithHint(
                    "Support minimum",
                    "Nombre minimal de recettes voisines contenant l'ingrédient pour le proposer."
                  )}
                  rules={[{ required: true, message: "Requis" }]}
                >
                  <InputNumber min={1} max={50} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="missing_min_frequency"
                  label={labelWithHint(
                    "Fréquence minimum (0–1)",
                    "Alternative au support : fréquence pondérée minimale d'apparition de l'ingrédient."
                  )}
                  rules={[{ required: true, message: "Requis" }]}
                >
                  <InputNumber min={0} max={1} step={0.05} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="missing_neighbor_threshold"
                  label={labelWithHint(
                    "Seuil de collecte des voisins (0–1)",
                    "Similarité minimale pour qu'une recette soit comptée comme voisine."
                  )}
                  rules={[{ required: true, message: "Requis" }]}
                >
                  <InputNumber min={0} max={1} step={0.05} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="missing_max_neighbors"
                  label={labelWithHint(
                    "Nombre max de voisins",
                    "Plafond de recettes voisines analysées (coût CPU)."
                  )}
                  rules={[{ required: true, message: "Requis" }]}
                >
                  <InputNumber min={1} max={100} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={handleSave}
            >
              Enregistrer
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              disabled={!defaults}
            >
              Réinitialiser aux valeurs par défaut
            </Button>
          </Space>
        </Form>
      )}
    </div>
  );
};

export default RecipeAssistantSettings;
